<template>
    <div>
        <dl class="mb-0">
            <div class="row">
                <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.type.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <jet-select
                        v-if="isEditable && types.length > 1"
                        v-model="form.type"
                        :disabled="isTypeEnforced"
                        :horizontal="false"
                        :inline="true"
                        :invalid-feedback="form?.errors.type"
                        :options="types"
                        :required="true"
                        class="mb-0 h-auto font-bold"
                    />
                    <span v-else>{{ selectedTypeLabel }}</span>
                </dd>
            </div>

            <div v-if="workOrder?.reportedBy" class="row">
                <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.reporter.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <div v-if="workOrder?.reportedBy">
                        <span class="font-bold">{{ workOrder?.reportedBy.name }}</span>
                        <span v-if="workOrder?.reportedAt">&nbsp;{{ trans('general.time.at') }} {{ formatInAirportTime(workOrder.reportedAt) }}</span>
                    </div>
                    <div v-else>{{ trans('general.none') }}</div>
                </dd>
            </div>

            <div v-if="catsaExtrasEnabled && form.metadata" class="row">
                <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.contact.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <inline-editable-field
                        v-model="form.metadata.contact_phone"
                        :editable="isEditable"
                        :invalid-feedback="form?.errors?.['metadata.contact_phone']"
                        :line-height="22"
                        :placeholder="trans('stakeholders.form.phone.placeholder')"
                        :resizable="false"
                    />
                </dd>
            </div>

            <div class="row">
                <dt :class="{ required: isAssigneeRequired }" class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.assignee.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <jet-select
                        v-model="form.assignee"
                        :disabled="!isAssigneeEditable || assignees.length === 0"
                        :horizontal="false"
                        :inline="true"
                        :invalid-feedback="form.errors.assignee"
                        :options="assignees"
                        :readonly="!isAssigneeEditable || assignees.length === 0"
                        :required="isAssigneeRequired"
                        :resettable="false"
                        class="mb-0 h-auto font-bold"
                        name="assignee"
                    >
                        <template #readonly>
                            {{ workOrder?.assignee?.name || trans('general.none') }}
                        </template>
                    </jet-select>
                    <div v-if="escalationInfo && isEditable" class="mt-1.5">
                        <div class="flex items-center gap-1 text-xs">
                            <template v-for="(step, index) in escalationInfo.stepLabels" :key="step.level">
                                <span v-if="index > 0" class="text-gray-light">&rarr;</span>
                                <span
                                    :class="
                                        step.level === escalationInfo.currentLevel
                                            ? 'rounded-md bg-button-primary px-1.5 py-0.5 font-medium text-primary'
                                            : 'text-muted'
                                    "
                                    >{{ step.label }}</span
                                >
                            </template>
                        </div>
                        <template v-if="escalationInfo.canEscalate">
                            <button
                                :class="
                                    isEscalationApplied
                                        ? 'cursor-default bg-button-primary text-muted'
                                        : 'bg-primary text-accent-foreground hover:bg-primary-hover'
                                "
                                :disabled="isEscalationApplied"
                                class="mt-2 inline-flex items-center !rounded-md px-2.5 py-0.75 text-[11px] leading-normal font-medium"
                                type="button"
                                @click="emit('escalate')"
                            >
                                {{
                                    trans('workorder.details.workorder.escalate.action', {
                                        level: escalationInfo.nextLevelLabel,
                                        name: escalationInfo.nextAssigneeName,
                                    })
                                }}
                            </button>
                            <p class="mt-1 text-[12px] text-muted">{{ escalationHint }}</p>
                        </template>
                    </div>
                </dd>
            </div>

            <div class="row">
                <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.start_date.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <jet-datepicker
                        v-model="form.startDate"
                        :deletable="true"
                        :horizontal="false"
                        :inline="true"
                        :invalid-feedback="form?.errors?.startDate"
                        :placeholder="trans('general.none')"
                        :readonly="!isEditable"
                    />
                </dd>
            </div>
            <div class="row">
                <dt :class="{ required: isDueDateRequired && isEditable }" class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.due_date.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <jet-datepicker
                        v-model="form.dueDate"
                        :deletable="true"
                        :horizontal="false"
                        :inline="true"
                        :invalid-feedback="form?.errors?.dueDate"
                        :min-date="form.startDate"
                        :placeholder="trans('general.none')"
                        :readonly="!isEditable"
                        :required="isDueDateRequired"
                    />
                </dd>
            </div>
            <div v-if="serviceDateEnabled" class="row">
                <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.service_date.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <jet-datepicker
                        v-model="form.serviceDate"
                        :deletable="true"
                        :horizontal="false"
                        :inline="true"
                        :invalid-feedback="form?.errors?.serviceDate"
                        :max-date="form.dueDate"
                        :min-date="form.startDate"
                        :placeholder="trans('general.none')"
                        :readonly="!isEditable"
                    />
                </dd>
            </div>
            <div v-if="showResolvedDate || showClosedDate" class="row">
                <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                    <span v-if="showResolvedDate">{{ trans('workorder.details.workorder.resolved_at.label') }}</span>
                    <span v-if="showClosedDate">{{ trans('workorder.details.workorder.closed_at.label') }}</span>
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <span v-if="showResolvedDate && workOrder.performedAt">{{ formatResolvedDate(workOrder.performedAt) }}</span>
                    <span v-else>{{ formatInAirportTime(workOrder.statusChangedAt) }}</span>
                </dd>
            </div>
            <div v-if="catsaExtrasEnabled" class="row">
                <dt :class="{ required: mspRespondedRequired && isEditable }" class="col-sm-4 col-12 text-[14px] !font-medium">
                    {{ mspRespondedLabel }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    <msp-responded-picker
                        v-model="form.mspRespondedAt"
                        :invalid-feedback="form?.errors?.mspRespondedAt"
                        :readonly="!isEditable"
                        :timezone="airportTimezone"
                    />
                </dd>
            </div>
            <div v-if="catsaExtrasEnabled && workOrder?.catsaCalledAt" class="row">
                <dt class="col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.catsa_called_at.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    {{ formatInAirportTime(workOrder.catsaCalledAt) }}
                </dd>
            </div>
            <div v-if="catsaExtrasEnabled && workOrder?.backInServiceAt" class="row">
                <dt class="col-sm-4 col-12 text-[14px] !font-medium">
                    {{ trans('workorder.details.workorder.back_in_service_at.label') }}
                </dt>
                <dd class="col-sm-8 col-12 text-muted">
                    {{ formatInAirportTime(workOrder.backInServiceAt) }}
                </dd>
            </div>
        </dl>
    </div>
</template>
<script setup>
import JetDatepicker from '@/Jetstream/Datepicker.vue';
import JetSelect from '@/Jetstream/Select.vue';
import InlineEditableField from '@/Pages/WorkOrders/Modals/InlineEditableField.vue';
import MspRespondedPicker from '@/Pages/WorkOrders/Modals/MspRespondedPicker.vue';
import { computed, getCurrentInstance } from 'vue';
import { trans } from '@/mixins/i18n.js';
import { DateTime } from 'luxon';
import fullDate from '@/filters/fullDate.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import { isCatsaExtrasApplicable } from '@/Pages/WorkOrders/Modals/catsaExtras.js';

const props = defineProps({
    form: {
        type: [Object, null],
    },
    workOrder: {
        type: [Object, null],
    },
    assignees: {
        type: Array,
        default: () => [],
    },
    types: {
        type: Array,
        default: () => [],
    },
    isTypeEnforced: {
        type: Boolean,
        default: false,
    },
    isEditable: {
        type: Boolean,
        default: true,
    },
    isAssigneeRequired: {
        type: Boolean,
        default: false,
    },
    // Server-computed: whether the current session may (re)assign this work order. Consumed by the
    // backend guard's twin (external stakeholders can't re-assign CATSA work orders — PETC-2865).
    // Defaults to true so non-external callers that don't pass it keep the assignee field editable.
    canReassign: {
        type: Boolean,
        default: true,
    },
    catsaExtras: { type: Object },
    workOrderPropertyConfig: {
        type: Object,
        default: () => ({}),
    },
    escalationInfo: {
        type: [Object, null],
        default: null,
    },
});

const emit = defineEmits(['escalate']);

// Once the escalation assignee is staged into the form (the unsaved-edits path), the button is
// "applied" — the user just needs to Save to persist it alongside their other changes.
const isEscalationApplied = computed(() => !!props.escalationInfo?.nextAssigneeId && props.form?.assignee === props.escalationInfo.nextAssigneeId);

const escalationHint = computed(() => {
    const name = props.escalationInfo?.nextAssigneeName;

    if (isEscalationApplied.value) {
        return trans('workorder.details.workorder.escalate.hint_applied', { name });
    }

    // With unsaved edits, escalating only stages the assignee; the bottom Save persists everything.
    if (props.form?.isDirty) {
        return trans('workorder.details.workorder.escalate.hint_unsaved', { name });
    }

    return trans('workorder.details.workorder.escalate.hint_immediate', { name });
});

// When the type can't be changed (existing work order, or only one creatable type
// is available) we show the type as static info instead of a dropdown.
const selectedTypeLabel = computed(() => {
    if (props.workOrder?.type?.label) {
        return props.workOrder.type.label;
    }

    const selected = props.types.find((type) => type.value === props.form?.type) ?? props.types[0];

    return selected?.label ?? trans('general.none');
});

const showResolvedDate = computed(() => props.workOrder?.status.value === 'done');
const showClosedDate = computed(() => props.workOrder?.status.value === 'closed');
const isDueDateRequired = computed(() => props.workOrder?.type.value === 'preventative_maintenance');
const catsaExtrasEnabled = computed(() => isCatsaExtrasApplicable(props.form?.type ?? props.workOrder?.type?.value, props.catsaExtras));

// The assignee field is editable only when the work order is editable and the server says this
// session may re-assign it. `canReassign` is the backend's decision (external stakeholders can't
// re-assign CATSA work orders — PETC-2865), so the rule lives in one place and can't drift.
const isAssigneeEditable = computed(() => props.isEditable && props.canReassign);

// Service date is only relevant for preventative-maintenance work orders, and only when the
// scheduling feature is enabled. It must stay within the work order's iteration window, so the
// picker is bounded by the start and due dates.
const effectiveWorkOrderType = computed(() => props.form?.type ?? props.workOrder?.type?.value);
const serviceDateEnabled = computed(
    () => hasFeature('schedule_preventative_maintenance_work_orders') && effectiveWorkOrderType.value === 'preventative_maintenance',
);

const airportTimezone = computed(() => props.workOrder?.timezone ?? 'UTC');

const mspRespondedConfig = computed(() => props.workOrderPropertyConfig?.msp_responded_at ?? {});
const mspRespondedLabel = computed(() => mspRespondedConfig.value?.ui?.label ?? trans('workorder.details.workorder.msp_responded_at.label'));
const mspRespondedRequired = computed(() => Boolean(mspRespondedConfig.value?.ui?.required));

const instance = getCurrentInstance();
const localeForFormat = computed(() => instance?.proxy?.currentLocale || 'en');

function formatInAirportTime(iso) {
    if (!iso) return '';
    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(airportTimezone.value).setLocale(localeForFormat.value);

    return `${dt.toLocaleString(DateTime.DATE_MED)}, ${dt.toFormat('HH:mm')}`;
}

function formatResolvedDate(iso) {
    return fullDate(iso, localeForFormat.value);
}
</script>
